import React, { useMemo } from 'react'
import { Avatar, Button, Checkbox, Table, Typography } from 'antd'
import { DeleteOutlined, UserOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { toDepartmentFilterOptions } from '@vacationtracker/shared/functions/department-options'

import IntlMessages from '../../../../util/IntlMessages'
import ImportPicker from './import-picker'
import { SegmentedToggle } from '../../../../design-system/SegmentedToggle'

import { IImportTableRow, IImportUsersTableProps } from '../types'

const { Text } = Typography

const ImportUsersTable = ({ rows, departments, locations, selectedRowKeys, onSelectChange, onUpdateRow, onRemoveRow }: IImportUsersTableProps): React.ReactElement => {
  const { formatMessage } = useIntl()

  const showDepartmentColumn = departments.length > 1
  const showLocationColumn = locations.length > 1
  const departmentOptions = useMemo(() => toDepartmentFilterOptions(departments), [departments])
  const locationOptions = useMemo(() => locations.map((location) => ({ value: location.id, label: location.name })), [locations])
  const yesNoOptions = useMemo(
    () => [
      { value: true, label: formatMessage({ id: 'app.yes' }) },
      { value: false, label: formatMessage({ id: 'app.no' }) },
    ],
    [formatMessage]
  )

  const columns = useMemo(() => {
    const cols: object[] = [
      {
        title: <IntlMessages id="app.user" />,
        dataIndex: 'name',
        key: 'user',
        render: (_: string, record: IImportTableRow) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar
              size={32}
              src={record.imageUrl || undefined}
              icon={!record.imageUrl ? <UserOutlined /> : undefined}
              style={{ ...(record.imageUrl ? {} : { backgroundColor: '#7f00ff' }), flexShrink: 0 }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <Text strong>{record.name}</Text>
              </div>
              {record.email && (
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.email}
                  </Text>
                </div>
              )}
            </div>
          </div>
        ),
      },
    ]

    if (showDepartmentColumn) {
      cols.push({
        title: <IntlMessages id="app.department" />,
        dataIndex: 'departmentId',
        key: 'department',
        width: 200,
        render: (value: string, record: IImportTableRow) => (
          <ImportPicker
            id={`department-${record.id}`}
            options={departmentOptions}
            value={value}
            placeholder={formatMessage({ id: 'app.department' })}
            onChange={(val) => onUpdateRow(record.id, 'departmentId', val)}
          />
        ),
      })
    }

    if (showLocationColumn) {
      cols.push({
        title: <IntlMessages id="app.location" />,
        dataIndex: 'locationId',
        key: 'location',
        width: 200,
        render: (value: string, record: IImportTableRow) => (
          <ImportPicker
            id={`location-${record.id}`}
            options={locationOptions}
            value={value}
            placeholder={formatMessage({ id: 'app.location' })}
            onChange={(val) => onUpdateRow(record.id, 'locationId', val)}
          />
        ),
      })
    }

    cols.push(
      {
        title: <IntlMessages id="components.importUsersForm.sendWelcomeMessageLabel" />,
        dataIndex: 'sendWelcomeMessage',
        key: 'welcomeMessage',
        width: 150,
        align: 'center' as const,
        render: (value: boolean, record: IImportTableRow) => (
          <SegmentedToggle size="mini" options={yesNoOptions} value={value} onChange={(next) => onUpdateRow(record.id, 'sendWelcomeMessage', next === true)} />
        ),
      },
      {
        title: '',
        key: 'remove',
        width: 60,
        render: (_: unknown, record: IImportTableRow) => <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onRemoveRow(record.id)} />,
      }
    )

    return cols
  }, [showDepartmentColumn, showLocationColumn, departmentOptions, locationOptions, formatMessage, onUpdateRow, onRemoveRow])

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  }

  const toggleRowSelection = (id: string) => {
    if (selectedRowKeys.includes(id)) {
      onSelectChange(selectedRowKeys.filter((k) => k !== id))
    } else {
      onSelectChange([...selectedRowKeys, id])
    }
  }

  return (
    <>
      {/* Desktop: standard table */}
      <div className="import-table-desktop">
        <Table
          rowKey="id"
          rowSelection={rowSelection}
          columns={columns}
          dataSource={rows}
          pagination={false}
          size="middle"
          scroll={{ x: 1000 }}
          locale={{
            emptyText: formatMessage({ id: 'components.importUsersForm.emptyTableMessage' }),
          }}
        />
      </div>

      {/* Mobile: card list */}
      <div className="import-table-mobile">
        {rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>{formatMessage({ id: 'components.importUsersForm.emptyTableMessage' })}</div>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="import-table-mobile-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Checkbox checked={selectedRowKeys.includes(row.id)} onChange={() => toggleRowSelection(row.id)} />
                <Avatar
                  size={32}
                  src={row.imageUrl || undefined}
                  icon={!row.imageUrl ? <UserOutlined /> : undefined}
                  style={!row.imageUrl ? { backgroundColor: '#7f00ff' } : undefined}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong>{row.name}</Text>
                  {row.email && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.email}
                      </Text>
                    </div>
                  )}
                </div>
                <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => onRemoveRow(row.id)} />
              </div>

              {showDepartmentColumn && (
                <div className="import-table-mobile-field">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <IntlMessages id="app.department" />
                  </Text>
                  <ImportPicker
                    id={`department-${row.id}`}
                    options={departmentOptions}
                    value={row.departmentId}
                    placeholder={formatMessage({ id: 'app.department' })}
                    onChange={(val) => onUpdateRow(row.id, 'departmentId', val)}
                  />
                </div>
              )}

              {showLocationColumn && (
                <div className="import-table-mobile-field">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <IntlMessages id="app.location" />
                  </Text>
                  <ImportPicker
                    id={`location-${row.id}`}
                    options={locationOptions}
                    value={row.locationId}
                    placeholder={formatMessage({ id: 'app.location' })}
                    onChange={(val) => onUpdateRow(row.id, 'locationId', val)}
                  />
                </div>
              )}

              <div className="import-table-mobile-field">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <IntlMessages id="components.importUsersForm.sendWelcomeMessageLabel" />
                </Text>
                <SegmentedToggle size="mini" options={yesNoOptions} value={row.sendWelcomeMessage} onChange={(next) => onUpdateRow(row.id, 'sendWelcomeMessage', next === true)} />
              </div>
            </div>
          ))
        )}
      </div>
    </>
  )
}

export default ImportUsersTable
